import { compact, uniq } from "lodash";
import { GetNewsSelectionFilesManagerVariables } from "src/api/generated/GetNewsSelectionFilesManager";
import { ISelectableRealestate } from "src/stores/RealestateStore";

// The recipients of a news, as Micro.News expects them in "role".
export enum NewsRecipientRole {
    COOWNER = 32,
    TENANT = 33
}

export const getNewsRecipientRole = (isCondominium: boolean): NewsRecipientRole =>
    isCondominium ? NewsRecipientRole.COOWNER : NewsRecipientRole.TENANT;

// The portal only combines realestates of one kind (the selection offers only that kind), the ERP sends one
// realestate per news. Older news can still mix both kinds, then STWEG wins and the news is for coowners.
// Realestates without access are null. Without any visible realestate, e.g. a new news from the page "News",
// it is a STWEG news, because the portal only creates STWEG news.
export const isCondominiumNews = (realestates: ({ condominium: boolean } | null)[]): boolean => {
    const visibleRealestates = compact(realestates);

    return visibleRealestates.length === 0 || visibleRealestates.some((realestate) => realestate.condominium);
};

// Files of a realestate: its own files and, if it is a consolidation (Mandant), the files of its realestates.
// For IT2 the files of the realestate's consolidation belong to it as well.
export const getNewsSelectionFilesVariables = (
    realestates: ISelectableRealestate[],
    isIT2: boolean
): GetNewsSelectionFilesManagerVariables => {
    const realestateIds = realestates.map((realestate) => realestate.id);
    const consolidationIds = isIT2
        ? realestates.flatMap((realestate) => (realestate.consolidationid ? [realestate.consolidationid] : []))
        : [];

    return {
        realestateids: uniq([...realestateIds, ...consolidationIds]),
        consolidationids: realestateIds
    };
};

export const getRemovedRealestates = (
    savedRealestates: ISelectableRealestate[],
    selectedRealestates: ISelectableRealestate[]
): ISelectableRealestate[] => {
    const selectedRealestateIds = new Set(selectedRealestates.map((realestate) => realestate.id));

    return savedRealestates.filter((realestate) => !selectedRealestateIds.has(realestate.id));
};

// Attached files that only belong to removed realestates. Files that also belong to a remaining realestate stay,
// and so do files from elsewhere, e.g. attached by the ERP.
export const getNewsFileIdsToDetach = (
    attachedFileIds: string[],
    fileIdsOfRemovedRealestates: string[],
    fileIdsOfRemainingRealestates: string[]
): string[] => {
    const removedRealestateFileIds = new Set(fileIdsOfRemovedRealestates);
    const remainingRealestateFileIds = new Set(fileIdsOfRemainingRealestates);

    return attachedFileIds.filter(
        (fileId) => removedRealestateFileIds.has(fileId) && !remainingRealestateFileIds.has(fileId)
    );
};
